
import { getAiResponse } from "@/utils/ai";
import { generateAndSaveBotWord } from "@/utils/gameDataUtils";
import { useToast } from "@/components/ui/use-toast";
import { WordPair } from "@/types/game";
import { supabase } from "@/integrations/supabase/client";
import { debugLog, DebugLevel } from "@/utils/debugUtils";

export const useBotWordGeneration = (
  gameId: string | null,
  setBotIsThinking: (value: boolean) => void,
  setBotHasChosenWord: (value: boolean) => void,
  usedWords: Set<string>,
  userHasSubmitted: boolean,
  setUserHasSubmitted: (value: boolean) => void,
  currentWord: string,
  rounds: WordPair[],
  handleProcessBothWords: (aiWord: string, aiExplanation: string | undefined) => Promise<void>
) => {
  const { toast } = useToast();

  const generateBotWordForCurrentRound = async (completedRounds: WordPair[]) => {
    if (completedRounds.length === 0 || !gameId) return;
    
    try {
      // Always set botIsThinking to true when generating a word
      setBotIsThinking(true);
      debugLog(DebugLevel.INFO, "botWordGeneration", "Bot is thinking set to TRUE");
      
      const lastRoundIndex = completedRounds.length - 1;
      const lastUserWord = completedRounds[lastRoundIndex].userWord;
      const lastBotWord = completedRounds[lastRoundIndex].aiWord;
      const currentRoundNumber = completedRounds.length + 1;
      
      // Get all used words
      const allUsedWords = Array.from(usedWords);
      
      // Generate and save the bot's word, passing game ID and round number for logging
      const { nextAiWord, explanation } = await generateAndSaveBotWord(
        gameId,
        currentRoundNumber,
        lastUserWord,
        lastBotWord,
        allUsedWords,
        // Pass a wrapper function that includes gameId and roundNumber
        (userWord: string, botWord: string, usedWordsArray: string[]) => 
          getAiResponse(userWord, botWord, usedWordsArray, gameId, currentRoundNumber)
      );
      
      debugLog(DebugLevel.INFO, "botWordGeneration", "Bot has chosen its word:", nextAiWord);
      
      // CRITICAL FIX: Set botHasChosenWord BEFORE checking if user has submitted
      // This ensures the useEffect in useWordSubmission will fire if user already submitted
      setBotHasChosenWord(true);
      
      // Double-check if user has submitted by querying the database directly
      const { data: roundData, error: roundError } = await supabase
        .from("rounds")
        .select("user_word")
        .eq("game_id", gameId)
        .eq("round_number", currentRoundNumber)
        .maybeSingle();
      
      if (roundError) {
        debugLog(DebugLevel.ERROR, "botWordGeneration", "Error checking if user has submitted:", roundError);
      }
      
      const userHasWordInDb = roundData && roundData.user_word && roundData.user_word.trim() !== "";
      debugLog(DebugLevel.INFO, "botWordGeneration", `User submission status - DB: ${userHasWordInDb ? "YES" : "NO"}, State: ${userHasSubmitted ? "YES" : "NO"}`);
      
      // Process both words if user has already submitted (either according to state or DB)
      if (userHasSubmitted || userHasWordInDb) {
        debugLog(DebugLevel.INFO, "botWordGeneration", "Bot finished generating word and user already submitted. Processing round immediately.");
        
        // Important fix: Keep bot thinking state TRUE until processing is complete
        // This ensures the checkmark stays visible until processing is done
        await handleProcessBothWords(nextAiWord, explanation);
        debugLog(DebugLevel.INFO, "botWordGeneration", "Successfully processed both words after bot generation");
      } else {
        debugLog(DebugLevel.INFO, "botWordGeneration", "Bot has chosen its word but user hasn't submitted yet. Waiting for user submission.");
        // Only set botIsThinking to false if user hasn't submitted yet
        setBotIsThinking(false);
        debugLog(DebugLevel.INFO, "botWordGeneration", "Bot is thinking set to FALSE (user hasn't submitted yet)");
      }
    } catch (error) {
      debugLog(DebugLevel.ERROR, "botWordGeneration", "Error generating bot word:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem generating the bot's word."
      });
      // Always reset thinking state on error
      setBotIsThinking(false);
      setBotHasChosenWord(false);
    }
  };

  return { generateBotWordForCurrentRound };
};
